/**
 * Duplicate Detection Utilities for Roster Import
 * 
 * This module provides comprehensive duplicate detection for dancer imports,
 * checking both within the imported list and against existing roster data.
 */

/**
 * Normalize a name for comparison (remove extra spaces, convert to lowercase)
 */
const normalizeName = (name) => {
  if (!name) return '';
  return name.toString().trim().toLowerCase().replace(/\s+/g, ' ');
};

/**
 * Normalize email for comparison
 */
const normalizeEmail = (email) => {
  if (!email) return '';
  return email.toString().trim().toLowerCase();
};

/**
 * Normalize date for comparison (convert to YYYY-MM-DD format)
 */
const normalizeDate = (date) => {
  if (!date) return '';
  
  // If already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }
  
  // If in MM-DD-YYYY format, convert to YYYY-MM-DD
  if (/^\d{2}-\d{2}-\d{4}$/.test(date)) {
    const [month, day, year] = date.split('-');
    return `${year}-${month}-${day}`;
  }
  
  return date;
};

/**
 * Generate a unique key for duplicate detection
 * Uses combination of normalized first name, last name, and date of birth
 */
const generateDuplicateKey = (member) => {
  const firstName = normalizeName(member.firstName);
  const lastName = normalizeName(member.lastName);
  const dateOfBirth = normalizeDate(member.dateOfBirth);
  
  return `${firstName}|${lastName}|${dateOfBirth}`;
};

/**
 * Generate an email key for email-based duplicate detection
 */
const generateEmailKey = (member) => {
  const email = normalizeEmail(member.email);
  return email || null;
};

/**
 * Check if two members are duplicates based on name and date of birth
 */
export const areDuplicateMembers = (member1, member2) => {
  const key1 = generateDuplicateKey(member1);
  const key2 = generateDuplicateKey(member2);
  return key1 === key2 && key1 !== '||'; // Ensure we don't match empty entries
};

/**
 * Check if two members have the same email (if both have emails)
 */
export const haveSameEmail = (member1, member2) => {
  const email1 = normalizeEmail(member1.email);
  const email2 = normalizeEmail(member2.email);
  return email1 && email2 && email1 === email2;
};

/**
 * Find duplicates within the imported list
 */
export const findInternalDuplicates = (members) => {
  const duplicates = [];
  const seenKeys = new Map();
  const seenEmails = new Map();

  members.forEach((member, index) => {
    const duplicateKey = generateDuplicateKey(member);
    const emailKey = generateEmailKey(member);
    
    // Check for name + DOB duplicates
    if (duplicateKey !== '||') { // Skip empty entries
      if (seenKeys.has(duplicateKey)) {
        const originalIndex = seenKeys.get(duplicateKey);
        duplicates.push({
          type: 'name_dob',
          indices: [originalIndex, index],
          members: [members[originalIndex], member],
          key: duplicateKey,
          message: `Duplicate found: ${member.firstName} ${member.lastName} (${member.dateOfBirth})`
        });
      } else {
        seenKeys.set(duplicateKey, index);
      }
    }
    
    // Check for email duplicates (separate from name/DOB duplicates)
    if (emailKey) {
      if (seenEmails.has(emailKey)) {
        const originalIndex = seenEmails.get(emailKey);
        // Only add if not already flagged as name/DOB duplicate
        const existingDuplicate = duplicates.find(d => 
          d.indices.includes(originalIndex) && d.indices.includes(index)
        );
        
        if (!existingDuplicate) {
          duplicates.push({
            type: 'email',
            indices: [originalIndex, index],
            members: [members[originalIndex], member],
            key: emailKey,
            message: `Email already used: ${emailKey}`
          });
        }
      } else {
        seenEmails.set(emailKey, index);
      }
    }
  });

  return duplicates;
};

/**
 * Find duplicates against existing roster
 */
export const findRosterDuplicates = (importMembers, existingRoster) => {
  const duplicates = [];

  importMembers.forEach((importMember, importIndex) => {
    existingRoster.forEach((existingMember, rosterIndex) => {
      // Check for name + DOB duplicates
      if (areDuplicateMembers(importMember, existingMember)) {
        duplicates.push({
          type: 'roster_name_dob',
          importIndex,
          rosterIndex,
          importMember,
          existingMember,
          message: `${importMember.firstName} ${importMember.lastName} (${importMember.dateOfBirth}) already exists in roster`
        });
      }
      // Check for email duplicates (if both have emails)
      else if (haveSameEmail(importMember, existingMember)) {
        duplicates.push({
          type: 'roster_email',
          importIndex,
          rosterIndex,
          importMember,
          existingMember,
          message: `Email ${importMember.email} is already used by ${existingMember.firstName} ${existingMember.lastName}`
        });
      }
    });
  });

  return duplicates;
};

/**
 * Comprehensive duplicate detection for import
 */
export const detectAllDuplicates = (importMembers, existingRoster = []) => {
  const internalDuplicates = findInternalDuplicates(importMembers);
  const rosterDuplicates = findRosterDuplicates(importMembers, existingRoster);
  
  return {
    internal: internalDuplicates,
    roster: rosterDuplicates,
    hasAnyDuplicates: internalDuplicates.length > 0 || rosterDuplicates.length > 0,
    totalDuplicates: internalDuplicates.length + rosterDuplicates.length
  };
};

/**
 * Get unique members after removing internal duplicates
 * Keeps the first occurrence of each duplicate
 */
export const getUniqueMembers = (members, internalDuplicates) => {
  const duplicateIndices = new Set();
  
  // Collect all duplicate indices except the first occurrence
  internalDuplicates.forEach(duplicate => {
    // Keep the first occurrence, mark others for removal
    duplicate.indices.slice(1).forEach(index => {
      duplicateIndices.add(index);
    });
  });
  
  return members.filter((_, index) => !duplicateIndices.has(index));
};

/**
 * Generate summary text for duplicates
 */
export const getDuplicateSummary = (duplicateData) => {
  const { internal, roster, totalDuplicates } = duplicateData;
  
  if (totalDuplicates === 0) {
    return "No duplicates found";
  }
  
  const summaryParts = [];
  
  if (internal.length > 0) {
    summaryParts.push(`${internal.length} duplicate${internal.length > 1 ? 's' : ''} within import`);
  }
  
  if (roster.length > 0) {
    summaryParts.push(`${roster.length} duplicate${roster.length > 1 ? 's' : ''} with existing roster`);
  }
  
  return summaryParts.join(', ');
};
